/**
 * Cálculo de poder y ejecución de efectos.
 *
 * Dos responsabilidades que van juntas porque comparten las mismas reglas:
 *
 * 1. **Cuánto poder tiene una carta ahora mismo.** Se distingue entre ajustes
 *    *permanentes* (los que un ON_REVEAL dejó grabados en `BoardCard.modifiers`)
 *    y ajustes *vivos* (ubicación y auras PASSIVE), que se recalculan en cada
 *    lectura. La diferencia importa: si el Bardo desapareciera del tablero, su
 *    aura se evapora sola sin tener que ir deshaciendo nada.
 *
 * 2. **Qué hace una carta al revelarse.** Un `switch` exhaustivo sobre la unión
 *    `CardEffect`: si mañana se añade una variante y no se maneja, TypeScript
 *    rompe la compilación en vez de ignorarla en silencio.
 *
 * Convenio: estas funciones MUTAN el estado que reciben. Quien clona es
 * `combatEngine`, siempre en la frontera pública.
 */

import { getCardMaster } from '@/data';
import type {
  BoardCard,
  CardEffect,
  CardInstance,
  GameState,
  LocationMaster,
  PlayerSide,
  PowerModifier,
  Zone,
} from '@/types/game';
import { MAX_CARDS_PER_ZONE } from '@/types/game';
import { pick } from './rng';
import {
  cardsOf,
  commitRng,
  drawCards,
  opponentOf,
  otherZoneIndices,
  revealedCardsOf,
  rngOf,
} from './state';

/* -------------------------------------------------------------------------- */
/* Poder                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Poder impreso más el bono de fusión.
 * Cada nivel por encima del 1 vale +1, tal y como lo aplicará `fuse_user_cards`
 * en el servidor (Fase 3). El cliente y el servidor tienen que coincidir aquí.
 */
export function basePowerOf(card: CardInstance): number {
  return getCardMaster(card.masterId).basePower + (card.level - 1);
}

/** Ajuste que la ubicación aplica a una carta concreta. 0 si no le afecta. */
export function locationDeltaFor(location: LocationMaster, card: CardInstance): number {
  const effect = location.effect;
  switch (effect.kind) {
    case 'POWER_DELTA_ALL':
      return effect.amount;
    case 'POWER_DELTA_BY_COST': {
      const cost = getCardMaster(card.masterId).cost;
      const matches =
        effect.comparator === 'LTE'
          ? cost <= effect.cost
          : effect.comparator === 'GTE'
            ? cost >= effect.cost
            : cost === effect.cost;
      return matches ? effect.amount : 0;
    }
    case 'NONE':
    case 'LOCK_ON_TURN':
      return 0;
  }
}

/**
 * Suma de las auras PASSIVE que afectan a esta carta.
 *
 * Solo cuentan las auras de cartas **ya reveladas** del mismo bando y de la
 * misma zona, y una carta nunca se buffea a sí misma.
 */
export function auraBonusFor(zone: Zone, card: BoardCard): number {
  let bonus = 0;
  for (const other of zone.cards) {
    if (other.instanceId === card.instanceId) continue;
    if (other.owner !== card.owner) continue;
    if (!other.revealed) continue;
    const effect = getCardMaster(other.masterId).effect;
    if (effect.kind === 'AURA_BUFF_ALLIES_HERE') bonus += effect.amount;
  }
  return bonus;
}

/**
 * Poder efectivo de una carta en el tablero.
 * Puede ser negativo: un debuff que hunde a una carta por debajo de cero es
 * información útil para el jugador, no un caso a esconder con un clamp.
 */
export function computeCardPower(zone: Zone, card: BoardCard): number {
  const permanent = card.modifiers.reduce((sum, modifier) => sum + modifier.amount, 0);
  return (
    basePowerOf(card) + permanent + locationDeltaFor(zone.location, card) + auraBonusFor(zone, card)
  );
}

/** Poder de un bando en una zona. Solo suman las cartas reveladas. */
export function computeZonePower(zone: Zone, side: PlayerSide): number {
  return revealedCardsOf(zone, side).reduce((sum, card) => sum + computeCardPower(zone, card), 0);
}

/** Poder total de un bando en las tres zonas: decide la iniciativa y el desempate. */
export function computeTotalPower(state: GameState, side: PlayerSide): number {
  return state.zones.reduce((sum, zone) => sum + computeZonePower(zone, side), 0);
}

/* -------------------------------------------------------------------------- */
/* Reglas de la ubicación                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Cartas que caben por jugador en la zona.
 *
 * Hoy es la misma constante para las tres. Sigue siendo una función y no un uso
 * directo de MAX_CARDS_PER_ZONE porque es el único punto por el que pasa la
 * pregunta "cuánto cabe aquí": el día que una carta o un modo de juego quiera
 * cambiarlo, se cambia aquí y no en los seis sitios que lo consultan.
 */
export function zoneCapacity(_zone: Zone): number {
  return MAX_CARDS_PER_ZONE;
}

/** Si la ubicación prohíbe jugar aquí en el turno indicado. */
export function isZoneLockedOnTurn(zone: Zone, turn: number): boolean {
  const effect = zone.location.effect;
  return effect.kind === 'LOCK_ON_TURN' && effect.turn === turn;
}

/* -------------------------------------------------------------------------- */
/* Efectos ON_REVEAL                                                          */
/* -------------------------------------------------------------------------- */

function addModifier(target: BoardCard, amount: number, source: BoardCard): void {
  if (amount === 0) return;
  const modifier: PowerModifier = {
    amount,
    sourceId: source.instanceId,
    sourceName: getCardMaster(source.masterId).name,
  };
  target.modifiers.push(modifier);
}

/** Formatea un entero con signo explícito: 3 -> "+3", -2 -> "-2". */
function signed(amount: number): string {
  return amount >= 0 ? `+${amount}` : `${amount}`;
}

/**
 * Ejecuta el ON_REVEAL de una carta que acaba de destaparse.
 *
 * Precondición: `card.revealed` ya es true (por eso los recuentos de aliados la
 * excluyen explícitamente).
 *
 * Devuelve la frase para el registro de combate, o null si el efecto no llegó a
 * hacer nada visible — así la UI no anima pasos vacíos.
 */
export function applyOnRevealEffect(state: GameState, card: BoardCard): string | null {
  const master = getCardMaster(card.masterId);
  const effect: CardEffect = master.effect;
  const zone = state.zones[card.zone];
  const enemy = opponentOf(card.owner);

  switch (effect.kind) {
    case 'NONE':
      return null;

    // Continua, no puntual: la calcula auraBonusFor en cada lectura.
    case 'AURA_BUFF_ALLIES_HERE':
      return `${master.name}: aura activa (${signed(effect.amount)} a sus aliados aquí)`;

    case 'DRAW_CARDS': {
      const drawn = drawCards(state, card.owner, effect.amount);
      if (drawn === 0) return `${master.name}: no puede robar (mazo o mano al límite)`;
      return `${master.name}: roba ${drawn} carta${drawn === 1 ? '' : 's'}`;
    }

    case 'GAIN_ENERGY_NEXT_TURN': {
      state.players[card.owner].bonusEnergyNextTurn += effect.amount;
      return `${master.name}: ${signed(effect.amount)} de energía el próximo turno`;
    }

    case 'BUFF_SELF_PER_ENEMY_HERE': {
      const enemies = revealedCardsOf(zone, enemy).length;
      const amount = enemies * effect.amountPer;
      if (amount === 0) return `${master.name}: sin enemigos aquí, sin bonificación`;
      addModifier(card, amount, card);
      return `${master.name}: ${signed(amount)} de poder (${enemies} enemigo${enemies === 1 ? '' : 's'})`;
    }

    case 'BUFF_SELF_IF_ALLIES_HERE': {
      const allies = revealedCardsOf(zone, card.owner).filter(
        (other) => other.instanceId !== card.instanceId,
      ).length;
      if (allies < effect.minAllies) {
        return `${master.name}: solo ${allies} aliado${allies === 1 ? '' : 's'} aquí, sin bonificación`;
      }
      addModifier(card, effect.amount, card);
      return `${master.name}: ${signed(effect.amount)} de poder (${allies} aliados)`;
    }

    case 'DEBUFF_SELF_PER_ALLY_HERE': {
      const allies = revealedCardsOf(zone, card.owner).filter(
        (other) => other.instanceId !== card.instanceId,
      ).length;
      const amount = -allies * effect.amountPer;
      if (amount === 0) return `${master.name}: llega solo, conserva todo su poder`;
      addModifier(card, amount, card);
      return `${master.name}: ${signed(amount)} de poder (${allies} aliado${allies === 1 ? '' : 's'} aquí)`;
    }

    case 'DEBUFF_RANDOM_ENEMY_HERE': {
      const targets = revealedCardsOf(zone, enemy);
      if (targets.length === 0) return `${master.name}: no hay objetivo en esta zona`;
      const rng = rngOf(state);
      const [target, nextRng] = pick(rng, targets);
      commitRng(state, nextRng);
      if (!target) return `${master.name}: no hay objetivo en esta zona`;
      addModifier(target, -effect.amount, card);
      return `${master.name}: -${effect.amount} de poder a ${getCardMaster(target.masterId).name}`;
    }

    case 'BUFF_ALLIES_OTHER_ZONES': {
      let affected = 0;
      for (const index of otherZoneIndices(card.zone)) {
        for (const ally of revealedCardsOf(state.zones[index], card.owner)) {
          addModifier(ally, effect.amount, card);
          affected += 1;
        }
      }
      if (affected === 0) return `${master.name}: no tienes cartas en las otras zonas`;
      return `${master.name}: ${signed(effect.amount)} de poder a ${affected} aliado${affected === 1 ? '' : 's'}`;
    }
  }
}

/**
 * Cartas de un bando en la zona contando también las pendientes de revelar.
 * Es lo que hay que mirar para la capacidad: una carta ya colocada ocupa sitio
 * aunque todavía esté boca abajo.
 */
export function occupancyOf(zone: Zone, side: PlayerSide): number {
  return cardsOf(zone, side).length;
}
